import { CaseFacility, DonorType, OrganApproval, OrganType } from '@/api/apis/case';

type TrialProgramName = 'ENHANCE' | 'DENOVO';

type TrialProgramLike =
  | {
      isActive?: boolean | null;
      name?: TrialProgramName;
    }
  | null
  | undefined;

type GetTrialFlowStateParams = {
  organType: OrganType;
  donorType: DonorType;
  txCenter?: CaseFacility;
  caseTrialProgram?: TrialProgramLike;
  organApproval?: OrganApproval | null;
  isEnhancePartA: boolean;
};

const hasActiveTxCenterTrialProgram = (
  programs: CaseFacility['trialPrograms'] | undefined,
  organType: OrganType,
  programName: TrialProgramName,
): boolean => {
  return (programs ?? []).some(
    (program) => program.isActive && program.organ === organType && program.name === programName,
  );
};

export const getTrialFlowState = ({
  organType,
  donorType,
  txCenter,
  caseTrialProgram,
  organApproval,
  isEnhancePartA,
}: GetTrialFlowStateParams) => {
  const txCenterTrialPrograms = txCenter?.trialPrograms;

  const hasMissingTrialData = caseTrialProgram == null || organApproval == null;

  const isHeartCase = organType === OrganType.Heart;
  const isLungsCase = organType === OrganType.Lungs;
  const isHeartDcdCase = isHeartCase && donorType === DonorType.DCD;
  const isHeartDbdCase = isHeartCase && donorType === DonorType.DBD;

  const hasEnhanceTrialProgram = hasActiveTxCenterTrialProgram(txCenterTrialPrograms, organType, 'ENHANCE');
  const hasDenovoTrialProgram = hasActiveTxCenterTrialProgram(txCenterTrialPrograms, organType, 'DENOVO');

  const isAllowedFirstTimeTrialFlow = isHeartDcdCase && hasEnhanceTrialProgram && isEnhancePartA;
  const isBlockedFirstTimeTrialFlow = isLungsCase || isHeartDbdCase || hasDenovoTrialProgram || hasEnhanceTrialProgram;

  const shouldBlockFirstTimeTrialQuestionnaire =
    hasMissingTrialData && !isAllowedFirstTimeTrialFlow && isBlockedFirstTimeTrialFlow;

  return {
    hasMissingTrialData,
    hasEnhanceTrialProgram,
    hasDenovoTrialProgram,
    isAllowedFirstTimeTrialFlow,
    isBlockedFirstTimeTrialFlow,
    shouldBlockFirstTimeTrialQuestionnaire,
  };
};
